import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../../hooks/useApi';
import { fmtDateTime } from '../../../utils/ruDates';

const TABS = [
  { key: 'api', label: 'API и биллинг' },
  { key: 'balance', label: 'Баланс' },
  { key: 'logs', label: 'Логи задач' },
];

const STATUS_LABELS = {
  pending: 'Ожидает',
  processing: 'В работе',
  done: 'Готово',
  error: 'Ошибка',
};

const STATUS_COLORS = {
  pending: { bg: '#fef9c3', fg: '#854d0e' },
  processing: { bg: '#dbeafe', fg: '#1e40af' },
  done: { bg: '#dcfce7', fg: '#166534' },
  error: { bg: '#fee2e2', fg: '#991b1b' },
};

function fmtDT(iso) {
  if (!iso) return '—';
  return fmtDateTime(iso);
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f3f4f6', fg: '#374151' };
  return (
    <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 12, background: c.bg, color: c.fg }}>
      {(STATUS_LABELS[status] || status || '—').toUpperCase()}
    </span>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: 'var(--surface, white)', borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: 24, ...style }}>
      {children}
    </div>
  );
}

// ── API & billing tab ────────────────────────────────────────────────────

function ApiTab() {
  const { request, showStatus } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    request('/api/v1/admin/parser-market/settings')
      .then(setCfg)
      .catch(() => showStatus('error', 'Ошибка загрузки настроек'))
      .finally(() => setLoading(false));
  }, [request]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        price_per_check_rub: Number(cfg.price_per_check_rub),
        stuck_task_timeout_minutes: Number(cfg.stuck_task_timeout_minutes),
        low_balance_threshold_rub: Number(cfg.low_balance_threshold_rub),
      };
      if (newKey.trim()) payload.api_key = newKey.trim();
      const saved = await request('/api/v1/admin/parser-market/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setCfg(saved);
      setNewKey('');
      showStatus('success', 'Настройки сохранены');
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) return <Card>Загрузка...</Card>;

  return (
    <Card style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600 }}>Сервис включён</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Общий рубильник — при выключении клиенты получат ошибку при попытке запустить проверку
          </div>
        </div>
        <input
          type="checkbox"
          checked={!!cfg.enabled}
          onChange={e => setCfg({ ...cfg, enabled: e.target.checked })}
          style={{ width: 20, height: 20 }}
        />
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>API-ключ parser.market</div>
        <div style={{ fontSize: 13, color: cfg.api_key_set ? '#166534' : '#991b1b', marginBottom: 8 }}>
          {cfg.api_key_set ? `Задан: ${cfg.api_key_masked}` : 'Не задан'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type={showKey ? 'text' : 'password'}
            placeholder="Вставьте новый ключ, чтобы заменить"
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd' }}
          />
          <button className="btn btn--ghost btn--sm" type="button" onClick={() => setShowKey(v => !v)}>
            {showKey ? 'Скрыть' : 'Показать'}
          </button>
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Цена за SKU, ₽</div>
        <input
          type="number" step="0.01" min="0"
          value={cfg.price_per_check_rub}
          onChange={e => setCfg({ ...cfg, price_per_check_rub: e.target.value })}
          style={{ width: 140, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd' }}
        />
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Таймаут зависшей задачи, мин</div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
          Если задача не завершится за это время — она автоматически помечается ошибкой, деньги возвращаются
        </div>
        <input
          type="number" min="5" step="5"
          value={cfg.stuck_task_timeout_minutes}
          onChange={e => setCfg({ ...cfg, stuck_task_timeout_minutes: e.target.value })}
          style={{ width: 140, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd' }}
        />
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Порог низкого баланса, ₽</div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
          Ниже этого значения снапшот баланса помечается как «низкий» на вкладке «Баланс»
        </div>
        <input
          type="number" min="0" step="10"
          value={cfg.low_balance_threshold_rub}
          onChange={e => setCfg({ ...cfg, low_balance_threshold_rub: e.target.value })}
          style={{ width: 140, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd' }}
        />
      </div>

      <div>
        <button className="btn btn--primary btn--sm" onClick={save} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
    </Card>
  );
}

// ── Balance tab ───────────────────────────────────────────────────────────

function BalanceTab() {
  const { request, showStatus } = useApi();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  const loadHistory = useCallback(() => {
    setLoading(true);
    request('/api/v1/admin/parser-market/balance/history?limit=50')
      .then(d => setHistory(d.snapshots || []))
      .catch(() => showStatus('error', 'Ошибка загрузки истории баланса'))
      .finally(() => setLoading(false));
  }, [request]); // eslint-disable-line

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const checkNow = async () => {
    setChecking(true);
    try {
      const data = await request('/api/v1/admin/parser-market/balance');
      setCurrent(data);
      loadHistory();
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card style={{ maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>Текущий баланс parser.market</div>
          <button className="btn btn--ghost btn--sm" onClick={checkNow} disabled={checking}>
            {checking ? 'Проверка...' : 'Обновить'}
          </button>
        </div>
        {current ? (
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, color: current.below_threshold ? '#991b1b' : '#166534' }}>
              {current.balance.toFixed(2)} ₽
            </div>
            {current.below_threshold && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>
                Баланс ниже порога {current.threshold.toFixed(2)} ₽ — пополните счёт на parser.market
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: '#666', fontSize: 13 }}>Нажмите «Обновить», чтобы запросить текущий баланс</div>
        )}
      </Card>

      <Card>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>История снапшотов (автосбор каждые 15 минут)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
            <tr>
              <th style={{ padding: 10, textAlign: 'left' }}>Дата</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Баланс</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Статус</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="3" style={{ padding: 30, textAlign: 'center' }}>Загрузка...</td></tr>}
            {!loading && history.length === 0 && (
              <tr><td colSpan="3" style={{ padding: 30, textAlign: 'center' }}>Снапшотов пока нет</td></tr>
            )}
            {history.map((h, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: 10 }}>{fmtDT(h.checked_at)}</td>
                <td style={{ padding: 10, textAlign: 'right' }}>{h.balance.toFixed(2)} ₽</td>
                <td style={{ padding: 10, textAlign: 'center' }}>
                  {h.below_threshold
                    ? <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 12, background: '#fee2e2', color: '#991b1b' }}>НИЗКИЙ</span>
                    : <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 12, background: '#dcfce7', color: '#166534' }}>ОК</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── Task log tab ──────────────────────────────────────────────────────────

function LogsTab() {
  const { request, showStatus } = useApi();
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (status) params.set('status', status);
    request(`/api/v1/admin/parser-market/tasks?${params.toString()}`)
      .then(d => { setTasks(d.tasks || []); setTotal(d.total || 0); })
      .catch(() => showStatus('error', 'Ошибка загрузки логов'))
      .finally(() => setLoading(false));
  }, [request, status, offset]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setBusyId(id);
    try {
      await request(`/api/v1/admin/parser-market/tasks/${id}/retry`, { method: 'POST' });
      load();
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setBusyId(null);
    }
  };

  const refund = async (id) => {
    setBusyId(id);
    try {
      await request(`/api/v1/admin/parser-market/tasks/${id}/refund`, { method: 'POST' });
      showStatus('success', 'Средства возвращены');
      load();
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <select value={status} onChange={e => { setStatus(e.target.value); setOffset(0); }} style={{ padding: '6px 10px', borderRadius: 6 }}>
          <option value="">Все статусы</option>
          <option value="pending">Ожидает</option>
          <option value="processing">В работе</option>
          <option value="done">Готово</option>
          <option value="error">Ошибка</option>
        </select>
        <span style={{ fontSize: 13, color: '#666' }}>{total} задач</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
            <tr>
              <th style={{ padding: 10, textAlign: 'left' }}>Дата</th>
              <th style={{ padding: 10, textAlign: 'left' }}>Компания / кабинет</th>
              <th style={{ padding: 10, textAlign: 'left' }}>Сценарий</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Статус</th>
              <th style={{ padding: 10, textAlign: 'right' }}>SKU</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Списано</th>
              <th style={{ padding: 10, textAlign: 'left' }}>Ошибка</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" style={{ padding: 30, textAlign: 'center' }}>Загрузка...</td></tr>}
            {!loading && tasks.length === 0 && (
              <tr><td colSpan="8" style={{ padding: 30, textAlign: 'center' }}>Задач нет</td></tr>
            )}
            {tasks.map(t => (
              <tr key={t.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: 10, fontSize: 13 }}>{fmtDT(t.created_at)}</td>
                <td style={{ padding: 10, fontSize: 13 }}>
                  <div>{t.company_name || t.company_id}</div>
                  <div style={{ color: '#666', fontSize: 12 }}>{t.account_name || t.account_id}</div>
                </td>
                <td style={{ padding: 10, fontSize: 13 }}>{t.scenario_name || '—'} <span style={{ color: '#666' }}>({t.region})</span></td>
                <td style={{ padding: 10, textAlign: 'center' }}><StatusBadge status={t.status} /></td>
                <td style={{ padding: 10, textAlign: 'right' }}>{t.completed_skus}/{t.total_skus}</td>
                <td style={{ padding: 10, textAlign: 'right' }}>{t.billed_amount.toFixed(2)} ₽</td>
                <td style={{ padding: 10, fontSize: 12, color: '#991b1b', maxWidth: 220 }}>{t.error_msg || ''}</td>
                <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {t.status === 'processing' && (
                    <button className="btn btn--ghost btn--xs" disabled={busyId === t.id} onClick={() => retry(t.id)}>
                      Опросить
                    </button>
                  )}
                  {t.status !== 'done' && (
                    <button className="btn btn--ghost btn--xs" disabled={busyId === t.id} onClick={() => refund(t.id)}>
                      Вернуть деньги
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
        <button className="btn btn--ghost btn--xs" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
          ← Назад
        </button>
        <button className="btn btn--ghost btn--xs" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
          Далее →
        </button>
      </div>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export function ParserMarketPanel({ embedded = false }) {
  const [tab, setTab] = useState('api');

  return (
    <div className="admin-page">
      {!embedded && (
        <div style={{ marginBottom: 24 }}>
          <h1>Парсинг (parser.market)</h1>
          <p style={{ color: '#666' }}>Ключ, включение сервиса, цена за SKU, баланс поставщика и лог задач по всем аккаунтам</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={`btn btn--sm ${tab === t.key ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'api' && <ApiTab />}
      {tab === 'balance' && <BalanceTab />}
      {tab === 'logs' && <LogsTab />}
    </div>
  );
}

export default ParserMarketPanel;
